import { getSurveyRule } from '@/services/surveyRule'
import AppWarn from '@/services/errors/app-warns'
import { idleTimeSurveyRequestFormatter, clearIdleTimerForSurveyRule, isTypeBOT } from '@/services/utils'
import updateChatWidget from '@/services/updateChatWidget'
import { surveyForm, chatEvents, cookieConstant, status, feedback, constantStrings } from '@/services/constants'
import { getCookie } from '../../services/cookie'
import { v4 as uuidV4 } from 'uuid'
import Storage from '@/services/storageLib'
import store from '@/store'

export default {
  updateSurveyRules({ commit }, data) {
    commit('UPDATE_SURVEY_RULES', data)
  },

  updateCSATState({ commit }, data) {
    commit('UPDATE_CSAT_STATE', data)
  },

  clearCSATState({ commit, dispatch }) {
    commit('UPDATE_CSAT_STATE', {
      isEnabled: false,
      rating: null,
      comment: '',
      thumbUpType: 'ThumbUp',
      thumbDownType: 'ThumbDown',
    })
    dispatch('resetCSATRateLimit')
  },

  reinitializeCSATRateLimit({ commit, dispatch, rootState }) {
    if (!Storage.getLocalStorageData(constantStrings.CSAT_RATE_LIMIT_STATE)) {
      return
    }
    const timeLeft = 60000 - (new Date().getTime() - Storage.getLocalStorageData(constantStrings.CSAT_RATE_LIMIT_STATE)?.limitStartTimestamp)
    if (timeLeft < 1 || timeLeft >= 60000) return
    const limitLeft = rootState.csat.rateLimit?.limitLeft
    const timerId = setTimeout(() => {
      dispatch('resetCSATRateLimit')
    }, timeLeft)
    commit('UPDATE_CSAT_STATE', { rateLimit: { limitLeft, timerId } })
  },

  resetCSATRateLimit({ commit }) {
    commit('UPDATE_CSAT_STATE', {
      rateLimit: {
        limitLeft: constantStrings.CSAT_RATE_LIMIT,
        timerId: null
      }
    })
    Storage.removeLocalStorageData(constantStrings.CSAT_RATE_LIMIT_STATE)
  },

  updateSurveyFormState({ commit }, data) {
    commit('UPDATE_SURVEY_FORM_STATE', data)
  },

  updateCSATRateLimit({ dispatch, commit, rootState }) {
    const previousLimitLeft = rootState.csat.rateLimit.limitLeft
    let timerId = rootState.csat.rateLimit.timerId
    if (!timerId) {
      timerId = setTimeout(() => {
        dispatch('resetCSATRateLimit')
      }, 60000)
      Storage.setLocalStorageData(constantStrings.CSAT_RATE_LIMIT_STATE, { limitStartTimestamp: new Date().getTime() })
    }
    const limitLeft = previousLimitLeft - 1
    commit('UPDATE_CSAT_STATE', { rateLimit: { limitLeft, timerId } })
    const currentRateLimitInLocalStorage = Storage.getLocalStorageData(constantStrings.CSAT_RATE_LIMIT_STATE)
    Storage.setLocalStorageData(constantStrings.CSAT_RATE_LIMIT_STATE, { ...currentRateLimitInLocalStorage, limitLeft })
  },

  updateCSATQuickReply({ rootState, dispatch }) {
    const isRatingPositive = rootState.csat.rating === feedback.POSITIVE
    const isRatingNegative = rootState.csat.rating === feedback.NEGATIVE
    const label = (isRatingPositive || isRatingNegative) ? rootState.widgetConfig.persistentSurveyConfig?.persistentSurveySettings?.textAfterFeedbackSent : rootState.widgetConfig.persistentSurveyConfig?.persistentSurveySettings?.textAfterFeedbackRemoved
    const quickReply = {
      label,
      metadata: surveyForm.WIDGET_EVENT_PERSISTENT_SURVEY,
      contentType: constantStrings.TEXT
    }
    dispatch('updateQuickReplyOptions', [ quickReply ])
  },

  updateCSATIcons({ dispatch, rootState }, data) {
    const isCSATFilledStatePositive = rootState.csat.rating === feedback.POSITIVE
    const isCSATFilledStateNegative = rootState.csat.rating === feedback.NEGATIVE
    if (data) {
      dispatch('updateCSATState', { thumbDownType: 'ThumbDown' })
      if (isCSATFilledStatePositive) {
        dispatch('updateCSATState', { thumbUpType: 'ThumbUp', rating: "" })
      } else {
        dispatch('updateCSATState', { thumbUpType: 'ThumbUpFilled', rating: feedback.POSITIVE })
      }
    } else {
      dispatch('updateCSATState', { thumbUpType: 'ThumbUp' })
      if (isCSATFilledStateNegative) {
        dispatch('updateCSATState', { thumbDownType: 'ThumbDown', rating: "" })
      } else {
        dispatch('updateCSATState', { thumbDownType: 'ThumbDownFilled', rating: feedback.NEGATIVE })
      }
    }
  },

  sendCSATSubmissionEvent({ dispatch }, data) {
    const eventData = { 
      eventType: chatEvents.CSAT_SURVEY,
      subType: chatEvents.SUBMIT,
      eventInfo: data
    }
    dispatch('sendEvent', { eventData })
    dispatch('throttleIdleChatTimeout') // Throttle chat session timeout
    dispatch('setIdleTimerForSurvey') // set idle timer for survey form
  },

  sendPersistentSurveyEvent({ dispatch }) {
    const eventData = { 
      eventType: chatEvents.WIDGET_EVENT, 
      subType: chatEvents.PERSISTENT_SURVEY,
    }
    dispatch('sendEvent', { eventData, eventName: chatEvents.INFO })
  },

  addPersistentSurveyInChats({ commit, rootState }) {
    const payload = rootState.widgetConfig?.persistentSurveyConfig
    const timestamp = Date.now()
    const request = {
      isUser: false,
      hide: false,
      messages:[{
        messageId: uuidV4(),
        type: surveyForm.SURVEY_FORM,
        isUser: false,
        timestamp,
        conversationId: getCookie(cookieConstant.SESSION.CONVERSATION_ID),
        messageValue: {
          schema: JSON.stringify({ payload }),
          status: [
            status.PENDING
          ]
        }
      }],
      timestamp
    }
    commit('UPDATE_CHATS', request)
  },

  hideSurveyOfPreviousConversation({ rootState, commit }) {
    const chats = rootState.chats
    for (let index=0; index<chats.length; index++) {
      const messagesLength = chats[index].messages?.length
      if (messagesLength) {
        for (let messageIndex=0; messageIndex<messagesLength; messageIndex++) {
          if (chats[index].messages[messageIndex].type === 'SurveyForm' && !chats[index].messages[messageIndex].hide) {
            chats[index].messages[messageIndex].hide = true
            commit('UPDATE_CHAT_AT_ITH_POS', {
              position: index,
              chatBlock: chats[index]
            })
            return
          }
        }
      }
    }
  },

  async setScheduleRulesForSuvey({ dispatch, rootState }) {
    try {
      if (!rootState.surveyRule.isPresent) {
        rootState.surveyRule.isPresent = true
        let rules = await getSurveyRule()
        if (!rules) return
        for (const rule of rules) {
          switch (rule.conversationTriggerType) {
            case surveyForm.WIDGET_EVENT_IDLE_USER:
              rule.expression = parseInt(rule.expression.substring(12)) * 1000
              dispatch('updateSurveyRules', { type: surveyForm.WIDGET_EVENT_IDLE_USER, rule })
              break;
            case surveyForm.WIDGET_EVENT_CHAT_END:
              dispatch('updateSurveyRules', { type: surveyForm.WIDGET_EVENT_CHAT_END, rule })
              break;
            case surveyForm.WIDGET_EVENT_SESSON_EXPIRE:
              dispatch('updateSurveyRules', { type: surveyForm.WIDGET_EVENT_SESSON_EXPIRE, rule })
              break;
            default:
              break;
          }
        }
      }
    } catch (error) {
      console.log('Error occurred while fetching survey rules config.', error.message)
    }
  },

  async setIdleTimerForSurvey({ dispatch, rootState }) {
    try {
      // don't throttle idle survey timer if user is connected to agentdesk
      if (!isTypeBOT(rootState.controlType)) {
        return
      }
      if (store.getters.isPromptTestingEnable) {
        return
      }
      await dispatch('setScheduleRulesForSuvey')
      clearIdleTimerForSurveyRule()
      const delay = rootState.surveyRule.idleTimeoutRule.rule?.expression
      if (!delay) return
      const timerId = setTimeout(() => {
        dispatch('sendEvent', idleTimeSurveyRequestFormatter(delay/1000))
        updateChatWidget.updateScript();
      }, delay)
      dispatch('updateSurveyRules', { type: surveyForm.WIDGET_EVENT_IDLE_USER, timerId })
    } catch (error) {
      new AppWarn({name: 'Action', message:`Error occurred while fetching survey rules config. ${error.message}`})
    }
    
  }
}
